import type { SummaryResult as SummaryResultType, SummaryInput } from '@creanizer/types'
import { Button } from '@creanizer/ui'
import { Card } from '@creanizer/ui'
import { Copy, Download, FileText } from 'lucide-react'
interface SummaryResultProps {
  result: SummaryResultType
  input: SummaryInput
}

export function SummaryResult({ result, input }: SummaryResultProps) {
  // 結果の検証
  if (!result || !result.summary) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>要約結果がありません</p>
      </div>
    )
  }
  
  // metadataのデフォルト値設定
  const metadata = result.metadata || {
    originalLength: 0,
    summaryLength: result.summary?.length || 0,
    compressionRatio: 0,
    textType: 'unknown',
    generatedAt: new Date().toISOString()
  }
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      // Toast notification removed - feature not available
    } catch (err) {
      console.error('クリップボードへのコピーに失敗しました', err)
    }
  }
  
  const exportAsFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  
  const generateFileName = (extension: string): string => {
    // 現在の日時を取得
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 19).replace(/[T:]/g, '-')
    
    // 要約の最初の20文字を取得して、ファイル名に適した形式に変換
    const preview = result?.summary || ''
      .slice(0, 20)
      .replace(/[^\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u3400-\u4DBFa-zA-Z0-9]/g, '_') // 日本語と英数字以外を_に変換
      .replace(/_+/g, '_') // 連続する_を1つにまとめる
      .replace(/^_|_$/g, '') // 先頭と末尾の_を削除
    
    return `summary_${dateStr}_${preview || 'text'}.${extension}`
  }
  
  const generateExportContent = (): string => {
    const sections = [
      '# テキスト要約',
      '',
      `生成日時: ${metadata.generatedAt}`,
      `元の文字数: ${metadata.originalLength.toLocaleString()}文字`,
      `要約後文字数: ${metadata.summaryLength.toLocaleString()}文字`,
      `圧縮率: ${Math.round(metadata.compressionRatio * 100)}%`,
      `テキストタイプ: ${metadata.textType}`,
      '',
      '## 要約本文',
      '',
      result?.summary || ''
    ]
    
    return sections.join('\n')
  }
  
  return (
    <div className="space-y-4">
      {/* メタデータ表示 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">元の文字数</div>
          <div className="text-lg font-semibold">
            {metadata.originalLength?.toLocaleString() || '0'}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">要約後</div>
          <div className="text-lg font-semibold">
            {metadata.summaryLength?.toLocaleString() || '0'}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">圧縮率</div>
          <div className="text-lg font-semibold">
            {Math.round((metadata.compressionRatio || 0) * 100)}%
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">テキストタイプ</div>
          <div className="text-lg font-semibold">
            {metadata.textType || 'unknown'}
          </div>
        </Card>
      </div>
      
      {/* 要約本文の表示 */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            要約本文
          </h4>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(result?.summary || '', '要約本文')}
              title="クリップボードにコピー"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="whitespace-pre-wrap text-sm leading-relaxed">
          {result?.summary || '（要約テキストなし）'}
        </div>
      </Card>
      
      {/* エクスポートボタン */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => exportAsFile(result?.summary || '', generateFileName('txt'))}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          テキストで保存
        </Button>
        <Button
          variant="outline"
          onClick={() => exportAsFile(generateExportContent(), generateFileName('md'))}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Markdownで保存
        </Button>
      </div>
    </div>
  )
}